import { useQuery, type QueryClient } from "@tanstack/react-query";
import { listAirports, getAirport } from "./api";
import type { Airport, AirportListQuery, PaginatedAirportItems } from "./types";

export const AirportsKeys = {
  all: ["airports"] as const,
  list: (query: AirportListQuery = {}) => ["airports", "list", query] as const,
  detail: (id: number | string) => ["airports", "detail", String(id)] as const,
};

export function useAirportsList(query: AirportListQuery = {}) {
  return useQuery<PaginatedAirportItems, Error>({
    queryKey: AirportsKeys.list(query),
    queryFn: () => listAirports(query),
    placeholderData: (previousData) => previousData,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

export function useAirportDetail(id: number | null) {
  const enabled = id != null;
  return useQuery<Airport, Error>({
    queryKey: enabled ? AirportsKeys.detail(id as number) : (["airports", "detail", "disabled"] as const),
    queryFn: () => {
      if (id == null) return Promise.reject(new Error("Airport id is required"));
      return getAirport(id);
    },
    enabled,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });
}

export function prefetchAirports(qc: QueryClient, query: AirportListQuery = {}) {
  return qc.prefetchQuery({
    queryKey: AirportsKeys.list(query),
    queryFn: () => listAirports(query),
    staleTime: 30_000,
  });
}
